import { RefObject, useLayoutEffect, useState } from "react";

// Height of the rendered product-name text, as a percent of the label
// container. Measured from the DOM because the char-width estimate can't
// predict where the browser actually wraps words — an over-estimate left an
// empty line at the bottom of the name box, before the SKU.
export const useMeasuredNameHeight = (
  containerRef: RefObject<HTMLElement>,
  nameId: string | undefined,
) => {
  const [percent, setPercent] = useState<number>();

  useLayoutEffect(() => {
    const container = containerRef.current;
    const text = nameId
      ? container?.querySelector<HTMLElement>(`[data-label-element="${nameId}"] p`)
      : null;

    if (!container || !text) {
      setPercent(undefined);

      return;
    }

    const measure = () => {
      const next = container.clientHeight
        ? (text.offsetHeight / container.clientHeight) * 100
        : undefined;

      setPercent(prev =>
        prev !== undefined && next !== undefined && Math.abs(prev - next) < 0.05 ? prev : next,
      );
    };

    measure();

    // Re-measure whenever the text re-wraps: edits, font size, box width,
    // web fonts finishing loading.
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(measure);

    observer.observe(text);

    return () => observer.disconnect();
  }, [containerRef, nameId]);

  return percent;
};
